// Basic
import { useState } from 'react';
// Libs
import { usePostHog } from '@posthog/react';
import { cn } from '@/common-utils/libraries/tailwind/utils';
// Components
import Entity from '@/common-utils/components/renders/entity/Entity';
import Icon from '@/common-utils/components/renders/icon/Icon';
import { Button } from '@/common-utils/components/ui/button';
// Common
import ICONS from '@/common-utils/enums/icons';

/**
 * @returns {JSX} ConsentBanner
 */
function ConsentBanner() {
    // Constants
    const CONSENT_GIVEN_PENDING = 'pending';
    const CONSENT_GIVEN_GRANTED = 'granted';
    const CONSENT_GIVEN_DENIED = 'denied';

    // PostHog instance
    const posthog = usePostHog();

    // State
    const [consentGiven, setConsentGiven] = useState(() => posthog?.get_explicit_consent_status());
    const [isExiting, setIsExiting] = useState(false);

    // Functions
    const handleAcceptCookies = (consentDecision) => {
        if (isExiting) {
            return;
        }

        if (consentDecision === CONSENT_GIVEN_GRANTED) {
            posthog?.opt_in_capturing();
        } else {
            posthog?.opt_out_capturing();
        }

        setIsExiting(true);
    };
    const handleExitAnimationEnd = (event) => {
        if (event.target !== event.currentTarget || !isExiting) {
            return;
        }

        setConsentGiven(posthog?.get_explicit_consent_status());
    };

    // Renderers
    if (consentGiven !== CONSENT_GIVEN_PENDING) {
        return null;
    }

    return (
        <div
            data-state={isExiting ? 'closed' : 'open'}
            onAnimationEnd={handleExitAnimationEnd}
            className={cn(
                'fixed flex flex-col items-center gap-3 left-0 right-0 bottom-0 z-[9999] bg-white/90 p-4 shadow-lg',
                'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-bottom data-[state=closed]:duration-300',
                isExiting && 'pointer-events-none'
            )}
            style={isExiting ? { animationFillMode: 'forwards' } : undefined}
            data-cy='consent-banner-container'
        >
            <Icon icon={ICONS.COOKIES} size={24} />
            <Entity
                entity='posthog.consent_banner.title'
                iconSize={16}
                className='text-center'
            />
            <div className='sm:flex-row sm:gap-6 justify-center flex gap-2 flex-col mt-1'>
                <Button
                    onClick={() => handleAcceptCookies(CONSENT_GIVEN_GRANTED)}
                    entity='posthog.consent_banner.accept'
                    variant='primary'
                />
                <Button
                    onClick={() => handleAcceptCookies(CONSENT_GIVEN_DENIED)}
                    entity='posthog.consent_banner.decline'
                    variant='secondary'
                />
            </div>
        </div>
    );
}

export default ConsentBanner;
